import { readWhen } from "../editor/event-time.js";
import { readMonths, shapeOf, writeMonths, type Month } from "../editor/calendar.js";

// A calendar box's panel in the page editor (docs/tapestry-design.md §3.5, stage 2b): its name, its months and their
// days, the day's hours, the era and which way years count; for any calendar but the first on the page, how it lines up
// with the first (shared points and a rate). Everything is written on the box's fence, like a thread's name: the text is
// where it lives, and the server checks it on save.

function h<K extends keyof HTMLElementTagNameMap>(tag: K, props: Partial<HTMLElementTagNameMap[K]> & { class?: string } = {}, ...children: Array<Node | string | null>): HTMLElementTagNameMap[K] {
  const el = document.createElement(tag);
  const { class: className, ...rest } = props;
  if (className) el.className = className;
  Object.assign(el, rest);
  for (const child of children) if (child !== null) el.append(child);
  return el;
}

export class P9CalendarBox extends HTMLElement {
  #settings: Record<string, string> = {};
  #drawing = false;
  /** Blank shared points added but not filled in yet (they aren't in the settings until they are). */
  #blankPoints = 0;

  connectedCallback(): void {
    this.#settings = this.#fence();
    this.#render();
  }

  /** Markdown mode: the fence was typed by hand; the panel follows it unless it's being typed in here. */
  syncSettings(json: string): void {
    if (this.dataset.settings === json) return;
    this.dataset.settings = json;
    if (this.contains(document.activeElement)) return;
    this.#settings = this.#fence();
    this.#render();
  }

  /** What this box's settings say, for the other calendars on the page to line up with. */
  get settings(): Record<string, string> {
    return this.#settings;
  }

  #fence(): Record<string, string> {
    try {
      return JSON.parse(this.dataset.settings ?? "{}") as Record<string, string>;
    } catch {
      return {};
    }
  }

  // The first calendar box on the page (in text order): the one the others line up with.
  #first(): P9CalendarBox | null {
    const editor = this.closest("p9-editor") ?? document;
    const all = [...editor.querySelectorAll<P9CalendarBox>("p9-calendar-box")].filter((b) => b.isConnected && b.offsetParent !== null);
    return all[0] ?? null;
  }

  #put(key: string, value: string, rerender = false): void {
    if (value) this.#settings[key] = value;
    else delete this.#settings[key];
    this.dispatchEvent(new CustomEvent("p9-tapestry-param", { bubbles: true, detail: { id: this.dataset.id, key, value } }));
    if (rerender) this.#render();
  }

  // Drawn once per turn: a field that loses focus while the panel is redrawn fires its change during the redraw.
  #render(): void {
    if (this.#drawing) return;
    this.#drawing = true;
    queueMicrotask(() => {
      this.#drawing = false;
      this.#draw();
    });
  }

  #draw(): void {
    const s = this.#settings;
    const title = h("input", { class: "tpe-title", type: "text", value: s.title ?? "", maxLength: 100, placeholder: "Name of the calendar" });
    title.addEventListener("change", () => this.#put("title", title.value.trim()));
    const head = h("div", { class: "tpe-head" }, h("span", { class: "tp-kind" }, "Calendar"), title,
      h("a", { class: "tpe-help", href: `${document.body.dataset.base ?? "/"}tapestry/help#calendars`, target: "_blank", title: "How calendars work", textContent: "?" }));

    const months = s.months ? readMonths(s.months) : null;
    const plain = h("input", { type: "checkbox", checked: !s.months });
    plain.addEventListener("change", () => {
      if (plain.checked) for (const key of ["months", "era", "count", "hours", "minutes"]) this.#put(key, "");
      else this.#put("months", "12 x 30");
      this.#render();
    });
    const sections: HTMLElement[] = [head,
      this.#section("Dates", "Ordinary dates are the ones we use. A calendar of its own has its own months; dates in it are still written as numbers (year-month-day hour:minute) and shown with its names.",
        h("label", { class: "small" }, plain, " ordinary dates (like ours)"))];

    if (s.months && !months) {
      sections.push(this.#section("Months", "The months can't be read here; fix them in the text (Markdown mode), e.g. months=\"Hammer 30, Midwinter 1\"."));
    } else if (months) {
      sections.push(this.#months(months), this.#shape());
    }

    const first = this.#first();
    if (first && first !== this) sections.push(this.#lineUp(first));
    else sections.push(this.#section("Lining up", "The first calendar on the page is the one the others line up with; any calendar after it says how its dates match this one's."));

    const shape = shapeOf(s);
    const pad = (n: number) => String(n).padStart(2, "0");
    const month = shape ? Math.min(3, shape.months.length) : 3;
    const example = shape ? `1012-${pad(month)}-${pad(Math.min(4, shape.months[month - 1]!.days))} ${pad(Math.min(21, shape.hours - 1))}:00` : "1912-03-03 21:00";
    sections.push(h("p", { class: "muted small" }, `A date here: ${example} reads as ${readWhen(example, shape) ?? "(not a date here)"}.`));
    this.replaceChildren(h("div", { class: "tpe" }, ...sections));
  }

  #section(title: string, hint: string, ...content: HTMLElement[]): HTMLElement {
    return h("div", { class: "tpe-section" }, h("p", { class: "tpe-label", textContent: title, title: hint }), h("p", { class: "muted small", textContent: hint }), ...content);
  }

  #months(months: Month[]): HTMLElement {
    const save = () => this.#put("months", writeMonths(months), true);
    const list = h("ol", { class: "tpe-rows" });
    months.forEach((m, i) => {
      const name = h("input", { type: "text", value: m.name, placeholder: `month ${i + 1} (no name)`, maxLength: 40 });
      name.addEventListener("change", () => ((m.name = name.value.trim()), save()));
      const days = h("input", { type: "number", value: String(m.days), min: "1", max: "10000", class: "tpe-number", title: "Days in it (1 for a festival day outside the months)" });
      days.addEventListener("change", () => ((m.days = Math.max(1, Math.min(10000, Number(days.value) || 1))), save()));
      const up = h("button", { type: "button", class: "small", textContent: "↑", title: "Move up", disabled: i === 0 });
      up.addEventListener("click", () => (([months[i - 1], months[i]] = [months[i]!, months[i - 1]!]), save()));
      const remove = h("button", { type: "button", class: "tpe-remove", textContent: "×", title: "Remove this month", disabled: months.length === 1 });
      remove.addEventListener("click", () => (months.splice(i, 1), save()));
      list.append(h("li", { class: "tpe-add" }, name, days, h("span", { class: "muted small" }, "days"), up, remove));
    });
    const add = h("button", { type: "button", class: "small", textContent: "Add month" });
    add.addEventListener("click", () => (months.push({ name: "", days: months[months.length - 1]?.days ?? 30 }), save()));
    const total = months.reduce((n, m) => n + m.days, 0);
    return this.#section("Months", `From the first of the year to the last; a festival day outside the months is a month of 1 day. ${months.length} months, ${total} days a year.`, list, add);
  }

  #shape(): HTMLElement {
    const s = this.#settings;
    const era = h("input", { type: "text", value: s.era ?? "", maxLength: 20, placeholder: "era, e.g. DR", class: "tpe-short" });
    era.addEventListener("change", () => this.#put("era", era.value.trim(), true));
    const down = h("input", { type: "checkbox", checked: s.count === "down" });
    down.addEventListener("change", () => this.#put("count", down.checked ? "down" : "", true));
    const number = (key: string, fallback: string, max: number, title: string) => {
      const input = h("input", { type: "number", value: s[key] ?? fallback, min: "1", max: String(max), class: "tpe-number", title });
      input.addEventListener("change", () => this.#put(key, input.value === fallback ? "" : String(Math.max(1, Math.min(max, Number(input.value) || Number(fallback)))), true));
      return input;
    };
    return this.#section("Years and days", "The era is shown after the year (1012 DR). Counting down: year 1000 comes before 999, like years before a great event.",
      h("div", { class: "tpe-add" }, era, h("label", { class: "small" }, down, " years count down")),
      h("div", { class: "tpe-add" }, h("span", { class: "muted small" }, "A day has"), number("hours", "24", 100, "Hours in a day"),
        h("span", { class: "muted small" }, "hours of"), number("minutes", "60", 100, "Minutes in an hour"), h("span", { class: "muted small" }, "minutes")));
  }

  // How this calendar lines up with the first: shared points (a date here = the same moment there) and a rate.
  #lineUp(first: P9CalendarBox): HTMLElement {
    const s = this.#settings;
    const firstName = first.settings.title || "the first calendar";
    const here = shapeOf(s);
    const there = shapeOf(first.settings);
    const points = (s.points ?? "").split(/[;\n]/).map((p) => p.split("=").map((x) => x.trim())).filter((p) => p.some(Boolean)) as string[][];
    for (let i = 0; i < this.#blankPoints; i++) points.push(["", ""]);
    const save = () => {
      this.#blankPoints = points.filter((p) => !p[0] && !p[1]).length;
      this.#put("points", points.filter((p) => p[0] || p[1]).map((p) => `${p[0] ?? ""} = ${p[1] ?? ""}`).join("; "), true);
    };
    const list = h("ul", { class: "tpe-rows" });
    points.forEach((p, i) => {
      const a = h("input", { type: "text", value: p[0] ?? "", placeholder: "a date here", class: "tpe-when" });
      const b = h("input", { type: "text", value: p[1] ?? "", placeholder: `the same moment in ${firstName}`, class: "tpe-when" });
      a.addEventListener("change", () => ((p[0] = a.value.trim()), save()));
      b.addEventListener("change", () => ((p[1] = b.value.trim()), save()));
      const reads = h("span", { class: "tpe-reads small" },
        [readWhen(p[0] ?? "", here) ?? "?", readWhen(p[1] ?? "", there) ?? "?"].join(" = "));
      const remove = h("button", { type: "button", class: "tpe-remove", textContent: "×", title: "Remove this point" });
      remove.addEventListener("click", () => (points.splice(i, 1), save()));
      list.append(h("li", { class: "tpe-add" }, a, h("span", {}, "="), b, remove, reads));
    });
    const add = h("button", { type: "button", class: "small", textContent: "Add a shared point" });
    add.addEventListener("click", () => (this.#blankPoints++, this.#render()));
    const rate = h("input", { type: "text", value: s.rate ?? "", placeholder: "1", class: "tpe-short", title: "Like 7, 0.5 or 1/7" });
    rate.addEventListener("change", () => this.#put("rate", rate.value.trim() === "1" ? "" : rate.value.trim(), true));
    return this.#section(`Lining up with ${firstName}`,
      `A shared point is a date here and the same moment in ${firstName}. Between two points time runs evenly (a jump or a faster stretch is one more point); before the first and after the last, at the rate.`,
      list, add,
      h("div", { class: "tpe-add" }, h("span", { class: "muted small" }, "For each day here,"), rate, h("span", { class: "muted small" }, `days pass in ${firstName}`)));
  }
}

customElements.define("p9-calendar-box", P9CalendarBox);
